/**
 * EventHubClient — thin wrapper over the generated AWSECEventHub TypeScript
 * client (@amzn/awseceventhub-client, `CMHKEventHubClient`). The generated
 * client handles restJson1 serialization, operation routing, SigV4 signing
 * (execute-api) and typed models; this module only wires up the aws-cn auth
 * chain and exposes the operations the app currently uses.
 *
 * Auth chain (mirrors EvalApiClient/IvtHubClient):
 *   Federate id_token → EventHub Cognito Identity Pool (aws-cn / cn-north-1)
 *   → temp AWS creds → generated client (SigV4, execute-api) → EventHub API.
 *
 * The id_token is the app's normal Federate session token (getIdToken) —
 * aud=`AgentHubPortal`, prod issuer. The EventHub pool is configured to trust
 * this prod-Federate identity directly, so no separate login is needed.
 *
 * The same id_token also rides on every request as `X-Id-Token` (signed with the
 * rest, see ID_TOKEN_HEADER). Event Hub does not authenticate with it — SigV4 does
 * that — it forwards it to Customer Link on ListCampaignMembers, so Customer Link can
 * authorize the operator by LDAP group rather than trusting Event Hub's role
 * (TT-CL-253, CR-316823719).
 */

import {
  CMHKEventHubClient,
  CreateCampaignNodeCommand,
  CreateEventCommand,
  CreateEventFileUploadCommand,
  DeleteCampaignNodeCommand,
  GetCampaignTreeCommand,
  ListCampaignRootsCommand,
  UpdateCampaignNodeCommand,
  ApproveNominationCommand,
  CreateNominationCommand,
  GetCurrentUserCommand,
  GetEventCommand,
  ListEventsCommand,
  ListNominationContactsCommand,
  ListEventAttendeesCommand,
  ListNominationsCommand,
  RejectNominationCommand,
  UpdateNominationAttendanceCommand,
  UpdateNominationCommand,
  UpdateEventCommand,
  PublishEventCommand,
  type CampaignNode,
  type CampaignTreeNode,
  type CreateCampaignNodeInput,
  type CreateEventInput,
  type CreateNominationInput,
  type AttendanceConfirmation,
  type Nomination,
  type UpdateNominationInput,
  type UpdateEventInput,
  type Event,
  type Contact,
  type ListNominationContactsCommandInput,
  type EventAttendee,
} from '@amzn/awseceventhub-client';
import { appConfig } from '../../config';
import { getIdToken } from '../../context/AuthContext';
import { API_REGION, getEventHubCredentials } from './eventHubCredentials';
import { currentEventHubImpersonation } from './eventHubImpersonation';

// ── Impersonation ───────────────────────────────────────────────────────────

/**
 * Admin impersonation. When set, every EventHub request carries the
 * `X-Event-Hub-Impersonate: <alias>` header; the service treats the request as
 * originating from that alias (see CR-305746938). This is fail-closed on the
 * server: only an `event-hub-admin` (ADMIN role) caller may impersonate — a
 * non-admin supplying the header is 403-denied. Authorization then runs against
 * the impersonated user's roles, not the admin's — see getCurrentUser.
 */
const IMPERSONATE_HEADER = 'x-event-hub-impersonate';
// Mirror the server-side format validation exactly (auth/Impersonation.java).
const ALIAS_PATTERN = /^[a-zA-Z0-9._-]{1,64}$/;

let _impersonateAlias: string | undefined;

/**
 * The caller's Federate id_token, forwarded by the service to Customer Link
 * (ForwardedIdToken.java in AWSECEventHub). The service keeps it only when its `sub` is the
 * real caller — under impersonation that is the admin, whose own token this is, so nothing
 * changes here when View as is on. Sent on every operation rather than ListEventAttendees
 * alone: the service reads it at its one choke point, and an op that does not reach Customer
 * Link ignores it. Must be in the API's CORS preflight allowlist (AWSECEventHubCDK
 * serviceStack.ts), or the browser blocks every call.
 */
const ID_TOKEN_HEADER = 'x-id-token';

/** Returns true if `alias` matches the server's accepted target format. */
export function isValidImpersonationAlias(alias: string): boolean {
  return ALIAS_PATTERN.test(alias);
}

/** The alias currently being impersonated, or undefined when off. */
export function getImpersonation(): string | undefined {
  return _impersonateAlias;
}

/**
 * Set (or clear, with undefined) the impersonation target for all subsequent
 * EventHub calls. The alias is format-validated before it is stored; an invalid
 * value throws rather than silently sending a header the server will reject.
 */
export function setImpersonation(alias: string | undefined): void {
  if (alias && !isValidImpersonationAlias(alias)) {
    throw new Error(
      `Invalid impersonation alias "${alias}" — must match ${ALIAS_PATTERN}`,
    );
  }
  _impersonateAlias = alias || undefined;
}

// ── Client ────────────────────────────────────────────────────────────────────

let _client: CMHKEventHubClient | undefined;

/**
 * Build (once) the generated EventHub client, wired to exchange the Federate
 * id_token for temp AWS creds via the EventHub Cognito Identity Pool (aws-cn).
 * A NotAuthorizedException / "Invalid login token" from the cred provider means
 * the OIDC provider or aud doesn't match what the pool trusts.
 */
function getClient(): CMHKEventHubClient {
  if (_client) return _client;

  const { apiEndpoint } = appConfig.eventHub;
  if (!apiEndpoint) {
    throw new Error(`No AWSECEventHub API endpoint configured for origin ${window.location.origin}`);
  }

  _client = new CMHKEventHubClient({
    // Sign with the API's region (ZHY), NOT the pool's (BJS).
    region: API_REGION,
    endpoint: apiEndpoint,
    credentials: getEventHubCredentials(),
  });

  // Inject the impersonation header on every request when an alias is active.
  // Read per request, so there is no need to rebuild the client when it changes.
  //
  // An explicit setImpersonation() wins; otherwise the target is derived from the portal's
  // View as at the moment the request is sent (see eventHubImpersonation for why it is not
  // pushed in from React).
  _client.middlewareStack.add(
    (next) => async (args) => {
      const req = args.request as { headers?: Record<string, string> };
      const alias = _impersonateAlias ?? currentEventHubImpersonation();
      if (alias && req && typeof req === 'object' && req.headers) {
        req.headers[IMPERSONATE_HEADER] = alias;
      }
      return next(args);
    },
    { step: 'build', name: 'eventHubImpersonationHeader' },
  );

  // Attach the id_token on every request. The build step runs before SigV4 signing, so the
  // header is covered by the signature. Omitted when there is no token: the service then just
  // has none to forward, and only the attendee list depends on it.
  _client.middlewareStack.add(
    (next) => async (args) => {
      const req = args.request as { headers?: Record<string, string> };
      const token = getIdToken();
      if (token && req && typeof req === 'object' && req.headers) {
        req.headers[ID_TOKEN_HEADER] = token;
      }
      return next(args);
    },
    { step: 'build', name: 'eventHubIdTokenHeader' },
  );

  return _client;
}

// ── Operations ─────────────────────────────────────────────────────────────

/**
 * Every event, following `nextToken` to exhaustion.
 *
 * `ListEvents` has no filter parameters at all — no date range, no status — so
 * callers that need a subset get the whole set and narrow it themselves. That is
 * affordable because the event count is a few hundred a year; revisit if the
 * operation ever grows real server-side filtering.
 *
 * The page cap is a guard against a malformed `nextToken` loop, not a business
 * limit: paging past it would silently truncate the calendar, so it throws instead.
 */
export async function listAllEvents(input?: { signal?: AbortSignal }): Promise<Event[]> {
  const MAX_PAGES = 100;
  const events: Event[] = [];
  let nextToken: string | undefined;
  let pages = 0;

  do {
    if (++pages > MAX_PAGES) {
      throw new Error(
        `ListEvents did not terminate after ${MAX_PAGES} pages — refusing to return a truncated event set`,
      );
    }
    const out = await getClient().send(
      new ListEventsCommand({ nextToken }),
      { abortSignal: input?.signal },
    );
    // `events` is documented as always present, but it is optional on the wire.
    events.push(...(out.events ?? []));
    nextToken = out.nextToken;
  } while (nextToken);

  return events;
}

/**
 * Calls `CreateEvent` and returns the created event.
 *
 * The response carries the whole resource, which is what makes the create-then-edit
 * flow possible: the caller needs the server-generated `eventId` to navigate to the
 * event it just created. Events are always created as DRAFT — publishing is a
 * separate `PublishEvent` call.
 */
export async function createEvent(input: CreateEventInput): Promise<Event> {
  const out = await getClient().send(new CreateEventCommand(input));
  if (!out.event) {
    throw new Error('CreateEvent returned no event');
  }
  return out.event;
}

/**
 * Calls `GetEvent` for one event.
 *
 * The detail page fetches by id rather than picking the event out of a cached list: the
 * URL is shareable, so it has to work on a cold load with no list in hand. It also means
 * the page shows the event as it is NOW, not as it was when some earlier list was taken.
 *
 * `signal` is threaded through so navigating away mid-flight aborts the request.
 */
export async function getEvent(
  eventId: string,
  input?: { signal?: AbortSignal },
): Promise<Event> {
  const out = await getClient().send(new GetEventCommand({ eventId }), {
    abortSignal: input?.signal,
  });
  if (!out.event) {
    // A 200 with no event would otherwise surface as a page of blank fields.
    throw new Error('GetEvent returned no event');
  }
  return out.event;
}

/**
 * Calls `ListNominationContacts` — one page of the contacts a BD may nominate.
 *
 * Scoped server-side to the caller's territories via the GCR Permission Service, so this
 * takes no filter: the answer is already "the ones you are allowed to nominate".
 *
 * Paginated (at most 100 per page): pass the previous page's `nextToken` until none comes back.
 */
export async function getNominationContacts(input?: {
  signal?: AbortSignal;
  nextToken?: string;
}): Promise<{ contacts: Contact[]; nextToken?: string }> {
  const command: ListNominationContactsCommandInput = input?.nextToken
    ? { nextToken: input.nextToken }
    : {};
  const out = await getClient().send(new ListNominationContactsCommand(command), {
    abortSignal: input?.signal,
  });
  return { contacts: out.contacts ?? [], nextToken: out.nextToken || undefined };
}

/**
 * Calls `ListEventAttendees` — one page of an event's attendees: the Customer Link campaign
 * members of its SFDC campaign, each joined server-side with the nomination for that guest, plus
 * plus, on the first page only, every nomination for the event. The service decides what each role
 * sees.
 *
 * Paginated (at most 100 attendees per page): pass the previous page's `nextToken` until none
 * comes back. `nominations` is the whole set, on the request without a `nextToken`, and absent
 * after; a nomination matched to a member is also that attendee's `nomination`.
 *
 * `includeSubCampaigns` also lists the campaigns beneath the event, one row per campaign
 * membership (utils/attendeeMerger merges them into people). The caller must send the same value
 * on every page: a token issued with it on is rejected with it off, and the reverse.
 */
export async function getEventAttendees(input: {
  eventId: string;
  signal?: AbortSignal;
  nextToken?: string;
  includeSubCampaigns?: boolean;
}): Promise<{ attendees: EventAttendee[]; nominations?: Nomination[]; nextToken?: string }> {
  const out = await getClient().send(
    new ListEventAttendeesCommand({
      eventId: input.eventId,
      nextToken: input.nextToken,
      ...(input.includeSubCampaigns ? { includeSubCampaigns: true } : {}),
    }),
    { abortSignal: input.signal },
  );
  return {
    attendees: out.attendees ?? [],
    nominations: out.nominations,
    nextToken: out.nextToken || undefined,
  };
}

/**
 * Calls `CreateNomination`, returning the created nomination.
 *
 * `clientToken` is the caller's to supply: a nomination is not idempotent by nature, and a
 * double-submitted form would otherwise put the same guest on the list twice.
 */
export async function createNomination(
  input: CreateNominationInput,
): Promise<Nomination> {
  const out = await getClient().send(new CreateNominationCommand(input));
  if (!out.nomination) {
    throw new Error('CreateNomination returned no nomination');
  }
  return out.nomination;
}

/**
 * Calls `UpdateEvent` with a partial event, returning the updated resource.
 *
 * PATCH semantics: an omitted field is left as it is. The service owns `contentStatus`
 * and will not accept it here, so this cannot publish or unpublish — that is
 * `PublishEvent` / `UnpublishEvent`.
 *
 * Returning the updated event matters for a form: the caller can re-seed its fields from
 * what the service actually stored, rather than from what it hoped it sent.
 */
export async function updateEvent(input: UpdateEventInput): Promise<Event> {
  const out = await getClient().send(new UpdateEventCommand(input));
  if (!out.event) {
    throw new Error('UpdateEvent returned no event');
  }
  return out.event;
}

/**
 * Uploads one file for an event and returns its id — for the description's `<img data-file-id>` or
 * `<a data-file-id>`, or anything else that references an event's file.
 *
 * Two steps: `CreateEventFileUpload` (signed, marketer/admin only) returns a short-lived presigned S3
 * URL and the headers to send with it, and the file is then PUT straight to S3 — the bytes never pass
 * through the API. The type, the exact size and how it will be served are signed into the URL, so the
 * PUT sends exactly the headers it was given. No credentials go with the PUT: the signature in the URL
 * is the authorization.
 *
 * The type is the browser's for the file, lower-cased; one it does not know is sent as
 * `application/octet-stream` (the service serves anything but images and PDF as a download anyway).
 */
export async function uploadEventFile(eventId: string, file: File): Promise<string> {
  const contentType = file.type.split(';')[0]!.trim().toLowerCase() || 'application/octet-stream';
  const out = await getClient().send(
    new CreateEventFileUploadCommand({
      eventId,
      contentType,
      contentLengthBytes: file.size,
      fileName: file.name ? file.name.slice(0, 255) : undefined,
      // One per upload: the SDK's own retries of this call then name the same file.
      clientToken: crypto.randomUUID(),
    }),
  );
  if (!out.fileId || !out.uploadUrl || !out.uploadHeaders) {
    throw new Error('CreateEventFileUpload returned no upload URL');
  }
  const put = await fetch(out.uploadUrl, {
    method: 'PUT',
    headers: out.uploadHeaders,
    body: file,
    credentials: 'omit',
  });
  if (!put.ok) {
    throw new Error(`The upload failed (${put.status})`);
  }
  return out.fileId;
}

/**
 * Calls `PublishEvent`, moving an event from DRAFT to PUBLISHED.
 *
 * Separate from create on purpose (the service owns `contentStatus` and will not take
 * it on a write input): an event is drafted, filled in, and only then published, at
 * which point it becomes visible to BDs and its nomination window starts to matter.
 * The service validates that the dates and nomination window are present before it
 * will publish.
 */
export async function publishEvent(eventId: string): Promise<Event> {
  const out = await getClient().send(new PublishEventCommand({ eventId }));
  if (!out.event) {
    throw new Error('PublishEvent returned no event');
  }
  return out.event;
}

/**
 * Calls `ListNominations` — the nominations this caller may see.
 *
 * No parameters at all: no page, no event filter, no status filter. The service decides
 * visibility from the caller (marketers and admins see every nomination; anyone else sees
 * their own plus those created by their direct and indirect reports, resolved through the
 * manager chain). So every surface fetches the same whole set and narrows it in memory, the
 * way ListEvents already works.
 *
 * Note the service reads the whole table to do this today — its own TODO says to move the
 * non-privileged path to a GSI query before real traffic.
 */
/**
 * Calls `GetCurrentUser` (`GET /me`): who the service treats this caller as.
 *
 * Only worth calling while impersonating. Then the service authorizes as the impersonated user,
 * with that user's roles, and nothing on the client carries those — the id token holds the
 * admin's own groups. Without impersonation the token's groups are the answer and this round
 * trip adds nothing.
 */
export async function getCurrentUser(input?: { signal?: AbortSignal }) {
  return getClient().send(new GetCurrentUserCommand({}), { abortSignal: input?.signal });
}

export async function listNominations(
  input?: { signal?: AbortSignal },
): Promise<Nomination[]> {
  const out = await getClient().send(new ListNominationsCommand({}), {
    abortSignal: input?.signal,
  });
  // Documented as always present, but optional on the wire.
  return out.nominations ?? [];
}

/**
 * Calls `ApproveNomination`, moving a PENDING_REVIEW nomination to APPROVED.
 *
 * Only a PENDING_REVIEW nomination can be decided: approving an already-approved one is an
 * idempotent no-op, but approving a REJECTED one is a 409 — a decision cannot be reversed.
 *
 * Authorization is wider than the marketer role: a manager of the nomination's creator may
 * also decide it. The frontend cannot know that relationship, so it cannot predict a 403 and
 * has to surface what the service says.
 *
 * Side effect: the service emails the creating BD, best-effort. Deciding is outwardly
 * visible, not just a status write.
 */
export async function approveNomination(nominationId: string): Promise<Nomination> {
  const out = await getClient().send(new ApproveNominationCommand({ nominationId }));
  if (!out.nomination) {
    throw new Error('ApproveNomination returned no nomination');
  }
  return out.nomination;
}

/**
 * Calls `RejectNomination`, moving a PENDING_REVIEW nomination to REJECTED.
 *
 * `rejectReason` is optional and reaches the BD in the rejection email. Note a second reject
 * does NOT overwrite the recorded reason — the service keeps the original and returns the
 * nomination unchanged.
 *
 * Same authorization and transition rules as {@link approveNomination}.
 */
export async function rejectNomination(
  nominationId: string,
  rejectReason?: string,
): Promise<Nomination> {
  const out = await getClient().send(
    new RejectNominationCommand({ nominationId, rejectReason }),
  );
  if (!out.nomination) {
    throw new Error('RejectNomination returned no nomination');
  }
  return out.nomination;
}

/**
 * Calls `UpdateNominationAttendance`: the nominating BD confirms, or withdraws the
 * confirmation, that an approved nomination's guest will attend.
 *
 * The service allows it only for the nomination's creator and only once it is APPROVED; anyone
 * else gets a 403 and any other status a 409. The UI offers the control on exactly those rows
 * (utils/attendance), so either error means the list was stale.
 */
export async function updateNominationAttendance(
  nominationId: string,
  attendanceConfirmation: AttendanceConfirmation,
): Promise<Nomination> {
  const out = await getClient().send(
    new UpdateNominationAttendanceCommand({ nominationId, attendanceConfirmation }),
  );
  if (!out.nomination) {
    throw new Error('UpdateNominationAttendance returned no nomination');
  }
  return out.nomination;
}

/**
 * Calls `UpdateNomination`: the nominating BD edits their nomination, which sends it back for
 * review.
 *
 * PATCH semantics: only the fields present are changed, and a present `accountInformation` /
 * `guestInformation` REPLACES the stored one whole — so the caller sends every field of an object
 * it changes (utils/nominationEdit `buildNominationPatch`). No clientToken: the operation is
 * idempotent, and an edit that changes nothing is a no-op that keeps the status.
 *
 * Only the creator, and only while the event takes nominations (utils/nominationEdit
 * `canEditNomination`); anyone else gets a 403, a closed event or a concurrent edit a 409.
 */
export async function updateNomination(input: UpdateNominationInput): Promise<Nomination> {
  const out = await getClient().send(new UpdateNominationCommand(input));
  if (!out.nomination) {
    throw new Error('UpdateNomination returned no nomination');
  }
  return out.nomination;
}

// ── Campaign hierarchy ──────────────────────────────────────────────────────

/**
 * The hierarchy node backing an event, or undefined when it has none
 * (`ListCampaignRoots?eventId=`). Root or child: the event may sit inside another event's tree.
 */
export async function findEventCampaignNode(
  eventId: string,
  input?: { signal?: AbortSignal },
): Promise<CampaignNode | undefined> {
  const out = await getClient().send(new ListCampaignRootsCommand({ eventId }), {
    abortSignal: input?.signal,
  });
  return out.nodes?.[0];
}

/**
 * Every hierarchy's root node (`ListCampaignRoots`, all pages). Used to work out which events
 * already back a node — the service has no per-event "has a node" field, so the picker of events
 * to place under another reads every tree. Same page-cap guard as {@link listAllEvents}.
 */
export async function listAllCampaignRoots(input?: { signal?: AbortSignal }): Promise<CampaignNode[]> {
  const MAX_PAGES = 100;
  const roots: CampaignNode[] = [];
  let nextToken: string | undefined;
  let pages = 0;
  do {
    if (++pages > MAX_PAGES) {
      throw new Error(
        `ListCampaignRoots did not terminate after ${MAX_PAGES} pages — refusing to return a truncated set`,
      );
    }
    const out = await getClient().send(new ListCampaignRootsCommand({ nextToken }), {
      abortSignal: input?.signal,
    });
    roots.push(...(out.nodes ?? []));
    nextToken = out.nextToken ?? undefined;
  } while (nextToken);
  return roots;
}

/** The subtree rooted at a node (`GetCampaignTree`). */
export async function getCampaignTree(
  nodeId: string,
  input?: { signal?: AbortSignal },
): Promise<CampaignTreeNode> {
  const out = await getClient().send(new GetCampaignTreeCommand({ nodeId }), {
    abortSignal: input?.signal,
  });
  if (!out.tree) throw new Error('GetCampaignTree returned no tree');
  return out.tree;
}

/** Creates a node (`CreateCampaignNode`) and returns it. */
export async function createCampaignNode(input: CreateCampaignNodeInput): Promise<CampaignNode> {
  const out = await getClient().send(new CreateCampaignNodeCommand(input));
  if (!out.node?.nodeId) throw new Error('CreateCampaignNode returned no node');
  return out.node;
}

/** Moves a node under another (`UpdateCampaignNode` with `parentNodeId`). */
export async function reparentCampaignNode(nodeId: string, parentNodeId: string): Promise<void> {
  await getClient().send(new UpdateCampaignNodeCommand({ nodeId, parentNodeId }));
}

/** Deletes a childless node (`DeleteCampaignNode`); one with children is a 409. */
export async function deleteCampaignNode(nodeId: string): Promise<void> {
  await getClient().send(new DeleteCampaignNodeCommand({ nodeId }));
}
